import { Country } from '@shopify/address/build/ts/index';
import axios, { AxiosError } from 'axios';
import React, { FC, useEffect, useState } from 'react';
import { BrowserRouter, Route, Switch, useLocation } from 'react-router-dom';
import { CUSTOMER_API_URL } from './constants';
import { SubscriptionDetail } from './pages/SubscriptionDetail';
import { SubscriptionAdd } from './pages/SubscriptionAdd';
import { SubscriptionCancel } from './pages/SubscriptionCancel';
import { ShippingAddress } from './pages/ShippingAddress';
import { ShippingAddressForm } from './pages/ShippingAddressForm';
import { PaymentMethod } from './pages/PaymentMethod';
import {SubscriptionCancelComplete} from "./pages/SubscriptionCancelComplete";
import {PaymentMethodComplete} from "./pages/PaymentMethodComplete";

export type Setting = {
  token: string;
  pageYOffset: number;
  resize: (height: number) => void;
};

type SubscriptionStatus = 'ACTIVE' | 'PAUSED' | 'CANCELLED';

export type SubscriptionLine = {
  productId: string;
  variantId: string;
  variantImage?: string;
  title: string;
  onlineStorePreviewUrl: string;
  variantTitle: string;
  sku?: string;
  quantity: number;
  currentPriceAmount: number;
  currentPriceCurrencyCode: string;
  lineDiscountedPriceAmount: number;
  lineDiscountedPriceCurrencyCode: string;
  lineId: string;
};

type BillingAttempt = {
  orderId?: string;
  orderToken?: string;
  orderName?: string;
  ready: boolean;
  createdAt: string;
  errorMessage?: string;
  errorCode?: string;
};

type RankGroup = {
  public: boolean;
};

type Shop = {
  showPause: boolean;
  showCancel: boolean;
  showSkip: boolean;
  showChangeAddress: boolean;
  mypageRemarks: string;
  showDelivery: boolean;
  deliveryDays: number;
  mypageChangeDeliveryDays: boolean;
  disableDeliveryDaysMon: boolean;
  disableDeliveryDaysTue: boolean;
  disableDeliveryDaysWed: boolean;
  disableDeliveryDaysThr: boolean;
  disableDeliveryDaysFri: boolean;
  disableDeliveryDaysSat: boolean;
  disableDeliveryDaysSun: boolean;
  showChangeProduct: boolean;
  showNewAddProduct: boolean;
  showChangePayment: boolean;
  showRemoveProduct: boolean;
  showNewOrder: boolean;
  rankGroup: RankGroup;
  showChangeQuantity: boolean;
  maxSkipCount: number;
  hideLowestBuy: boolean;
};

type Rank = {
  name: string;
  discountPercentage: number;
};

export type Subscription = {
  customerId: string;
  originOrderId: string;
  originOrderToken: string;
  originOrderName: string;
  customerDisplayName: string;
  customerEmail: string;
  customerPaymentMethodId?: string;
  nextBillingDate: Date;
  subscriptionContractId: string;
  billingPolicyInterval: 'DAY' | 'WEEK' | 'MONTH' | 'YEAR';
  billingPolicyIntervalCount: number;
  billingPolicyMinCycles: number;
  billingPolicyMaxCycles: number;
  status: SubscriptionStatus;
  subscriptionLines: SubscriptionLine[];
  billingAttempts: BillingAttempt[];
  deliveryPriceAmount: number;
  deliveryPriceCurrencyCode: string;
  deliveryAddress1: string;
  deliveryAddress2: string;
  deliveryCity: string;
  deliveryCompany: string;
  deliveryCountry: string;
  deliveryCountryCode: string;
  deliveryFirstName: string;
  deliveryLastName: string;
  deliveryName: string;
  deliveryPhone: string;
  deliveryProvince: string;
  deliveryProvinceCode: string;
  deliveryZip: string;
  deliveryDays: number;
  createdAt: string;
  disabled: boolean;
  deliveryFormattedAddress: string[];
  countries: Country[];
  country: Country;
  paymentType: string;
  shop: Shop;
  rank: Rank;
};

export const statusMessages = {
  ACTIVE: 'アクティブ',
  PAUSED: '一時停止',
  CANCELLED: '解約',
  EXPIRED: '期限切れ',
  FAILED: '失敗',
};
const History: FC<{ children: any }> = ({ children }) => {
  const pathname = useLocation().pathname;

  useEffect(() => {
    window.parent.postMessage({ action: 'pushHistory', path: pathname }, '*');
  }, [pathname]);

  return children;
};

export const App = () => {
  const [config, setConfig] = useState<{
    token: string;
    pageYOffset: number;
    styleTexts: string[];
    styleUrls: string[];
  }>();

  const resize = (height: number) => {
    window.parent.postMessage({ action: 'resetWindow', height }, '*');
  };

  useEffect(() => {
    const initializeEvent = (event: MessageEvent) => {
      // We can't rejected by event.origin because Shopify Store domain is not unique.
      // So, We check data structure only.
      const { code, styleTexts, styleUrls } = event.data;
      if (!code || !styleTexts || !styleUrls) {
        return;
      }

      axios
        .post(`${CUSTOMER_API_URL}?action=token`, {
          code,
        })
        .then((response) => {
          window.removeEventListener('message', initializeEvent);

          const { styleTexts, styleUrls } = event.data;
          setConfig({ token: response.data, pageYOffset: 0, styleTexts, styleUrls });
        })
        .catch((error: AxiosError) => {
          if (!error.response) {
            // Debug for now
            throw new Error(`Network Error but not response: ${error.message}`);
          }

          if (error.response.status >= 500) {
            throw error;
          }
        });
    };
    window.addEventListener('message', initializeEvent);

    window.addEventListener('resize', () => {
      // Safari doesn't compute "float: left" height.
      if (!document.querySelector('body')!.scrollHeight) {
        return;
      }

      const height = document.querySelector('body')!.scrollHeight + 100; // buffer;

      resize(height);
    });
    window.parent.postMessage({ action: 'initialize' }, '*');

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!config) {
    return <>認証中です。少々お待ちください。</>;
  }

  const setting = {
    token: config.token,
    pageYOffset: config.pageYOffset,
    resize,
  };

  config.styleTexts.forEach((styleText) => {
    const style = document.createElement('style');
    style.innerText = styleText;

    document.body.appendChild(style);
  });

  config.styleUrls.forEach((url) => {
    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = url;

    document.body.appendChild(link);
  });

  return (
    <BrowserRouter basename="/?path=">
      <History>
        <Switch>
          <Route path="/subscription/add">
            <SubscriptionAdd setting={setting} />
          </Route>
          <Route path="/shippingAddress/form">
            <ShippingAddressForm setting={setting} />
          </Route>
          <Route path="/shippingAddress">
            <ShippingAddress setting={setting} />
          </Route>
          <Route path="/paymentMethod/complete">
            <PaymentMethodComplete setting={setting} />
          </Route>
          <Route path="/paymentMethod">
            <PaymentMethod setting={setting} />
          </Route>
          <Route path="/cancel/complete">
            <SubscriptionCancelComplete setting={setting} />
          </Route>
          <Route path="/cancel">
            <SubscriptionCancel setting={setting} />
          </Route>
          <Route path="/">
            <SubscriptionDetail setting={setting} />
          </Route>
        </Switch>
      </History>
    </BrowserRouter>
  );
};
